/**
 * Small markers and buttons for the Skip or Always 0 card. Always 0 takes the
 * one Always 0 tone (`push-rule-styles.ts`) with the Ban glyph; Skip is
 * neutral (Shopify just keeps its number) with PauseCircle. The glyph and the
 * word ride with every colour (DESIGN.md 16).
 */
import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { AlertTriangle, Archive, Ban, Layers, Link2, Loader2, PauseCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ProductionTypeBadge } from '@/features/inventory/components/ProductionTypeBadge';
import { cn } from '@/lib/utils';
import { skuKind, type PushRuleStatus, type PushSkuRule } from '../utils/push-sku-rules';
import { ALWAYS_ZERO_BADGE_CLASS, ALWAYS_ZERO_BUTTON_CLASS } from './push-rule-styles';

/** The rule on a row. `dimmed`: the rule does nothing any more (an inert row). */
export function RuleBadge({ rule, dimmed = false, className }: { rule: PushSkuRule; dimmed?: boolean; className?: string }) {
  const { t } = useTranslation('inventory');
  const zero = rule === 'always_zero';
  const Icon = zero ? Ban : PauseCircle;
  return (
    <Badge
      variant="outline"
      className={cn(
        'shrink-0 gap-1 whitespace-nowrap px-1.5 py-0 text-[11px] font-medium',
        zero ? ALWAYS_ZERO_BADGE_CLASS : 'text-foreground',
        dimmed && 'opacity-60',
        className,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {zero ? t('stockSync.pushRules.rule.alwaysZero') : t('stockSync.pushRules.rule.skip')}
    </Badge>
  );
}

/**
 * Under a row's rule badge, when its SKU is not a plain live one.
 *  - Retired: a fact, not a fault. The push still applies the rule, so the
 *    line is muted and just says "Retired".
 *  - A pre-printed design: the push applies the rule to the design's own
 *    stock rows only, so it partly applies. "Pre-printed design", then "Only
 *    partly applies. Set its base garment instead.", in the warning tone.
 *  - Anything else does nothing: "Does nothing: Deleted SKU", in the warning
 *    tone. The words come from `stockSync.pushRules.status.*`, which also names
 *    a row with no SKU code.
 */
export function RuleStatusLine({ status }: { status: PushRuleStatus }) {
  const { t } = useTranslation('inventory');
  if (status === 'ok') return null;
  if (status === 'retired') {
    return (
      <span className="flex items-start gap-1 text-xs text-muted-foreground">
        <Archive className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
        {t('stockSync.pushRules.status.retired')}
      </span>
    );
  }
  if (status === 'preprinted') {
    return (
      <span className="flex items-start gap-1 text-xs text-foreground">
        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-700" aria-hidden="true" />
        <span className="flex flex-col">
          <span className="font-medium">{t('stockSync.pushRules.status.preprinted')}</span>
          <span>{t('stockSync.pushRules.list.preprintedPartly')}</span>
        </span>
      </span>
    );
  }
  return (
    <span className="flex items-start gap-1 text-xs text-foreground">
      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-700" aria-hidden="true" />
      {t('stockSync.pushRules.list.doesNothing', { reason: t(`stockSync.pushRules.status.${status}`) })}
    </span>
  );
}

/** "Base garment": a rule on one reaches every design printed on it. */
export function BaseGarmentChip() {
  const { t } = useTranslation('inventory');
  return (
    <Badge
      variant="outline"
      className="shrink-0 gap-1 whitespace-nowrap px-1.5 py-0 text-[11px] font-medium text-foreground"
    >
      <Layers className="h-3 w-3" aria-hidden="true" />
      {t('stockSync.pushRules.baseGarment')}
    </Badge>
  );
}

/** "3PL garment": the group is the client's copy of a garment its 3PL shares (a pick covers every colour and size). */
export function SharedGarmentChip() {
  const { t } = useTranslation('inventory');
  return (
    <Badge
      variant="outline"
      className="shrink-0 gap-1 whitespace-nowrap px-1.5 py-0 text-[11px] font-medium text-foreground"
    >
      <Link2 className="h-3 w-3" aria-hidden="true" />
      {t('stockSync.pushRules.sharedGarment')}
    </Badge>
  );
}

/** The ONE type a SKU shows: Base garment, else POD or PAP, else a muted dash. */
export function SkuKindLabel({ productionType, isBaseGarment }: { productionType: string | null; isBaseGarment: boolean }) {
  const kind = skuKind({ productionType, isBaseGarment });
  if (kind === 'base_garment') return <BaseGarmentChip />;
  if (kind) return <ProductionTypeBadge productionType={kind} />;
  return <span className="text-muted-foreground">-</span>;
}

interface RuleButtonProps extends Omit<ComponentPropsWithoutRef<typeof Button>, 'children'> {
  rule: PushSkuRule;
  /** The row already has this rule: the button is disabled and says so. */
  already?: boolean;
  pending?: boolean;
  /** Read after the visible words (WCAG 2.5.3), so rows with the same button tell apart. */
  srContext?: ReactNode;
}

/**
 * [Skip] or [Always 0] on a search row. Skip is a neutral outline with
 * PauseCircle; Always 0 takes the Always 0 tone with Ban. A row that already
 * has the rule shows "Skipped" / "Is Always 0", disabled.
 */
export const RuleButton = forwardRef<HTMLButtonElement, RuleButtonProps>(function RuleButton(
  { rule, already = false, pending = false, srContext, className, disabled, ...props },
  ref,
) {
  const { t } = useTranslation('inventory');
  const zero = rule === 'always_zero';
  const Icon = pending ? Loader2 : zero ? Ban : PauseCircle;
  const label = already
    ? zero
      ? t('stockSync.pushRules.search.isAlwaysZero')
      : t('stockSync.pushRules.search.skipped')
    : zero
      ? t('stockSync.pushRules.rule.alwaysZero')
      : t('stockSync.pushRules.rule.skip');
  return (
    <Button
      ref={ref}
      type="button"
      variant="outline"
      className={cn('h-8 gap-1.5 px-3 text-xs', zero && ALWAYS_ZERO_BUTTON_CLASS, className)}
      disabled={already || disabled}
      aria-busy={pending || undefined}
      {...props}
    >
      <Icon
        className={cn('h-3.5 w-3.5', pending && 'animate-spin motion-reduce:animate-none')}
        aria-hidden="true"
      />
      {label}
      {srContext && <span className="sr-only"> {srContext}</span>}
    </Button>
  );
});
